import { useState, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Chip,
    Alert,
    InputAdornment,
    Tabs,
    Tab,
    Paper,
    ToggleButtonGroup,
    ToggleButton,
    Divider,
    CircularProgress,
} from '@mui/material'
import { Send, Email as EmailIcon, Visibility, Edit, Language } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { emailApi } from '@api/email'
import { formsApi } from '@api/forms'
import { useTranslation } from '@i18n'
import { toast } from 'react-toastify'

const EmailModal = ({ open, onClose, submission }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    // Extract email from submission data
    const getSubmissionEmail = () => {
        if (!submission?.data) return ''
        const data = submission.data
        const emailFields = ['email', 'Email', 'email_address', 'emailAddress', 'user_email', 'contact_email']
        for (const field of emailFields) {
            if (data[field] && typeof data[field] === 'string' && data[field].includes('@')) {
                return data[field]
            }
        }
        for (const [key, value] of Object.entries(data)) {
            if (typeof value === 'string' && value.includes('@') && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                return value
            }
        }
        return ''
    }

    const [activeTab, setActiveTab] = useState(0) // 0 = Compose, 1 = Preview
    const [emailLanguage, setEmailLanguage] = useState('both') // 'en', 'ar', 'both'
    const [formData, setFormData] = useState({
        to: '',
        subject: '',
        message: '',
        subjectAr: '',
        messageAr: '',
    })

    // Update email when submission changes or modal opens
    useEffect(() => {
        if (open && submission) {
            const email = getSubmissionEmail()
            setFormData(prev => ({ ...prev, to: email }))
            setActiveTab(0)
        }
    }, [open, submission])

    // Fetch form settings for email header
    const { data: formSettings } = useQuery({
        queryKey: ['form', submission?.form_id],
        queryFn: () => formsApi.getById(submission?.form_id),
        enabled: !!submission?.form_id && open,
        select: (data) => data?.data?.settings || data?.settings || {}
    })

    // Get email header settings from form
    const emailHeaderSettings = {
        enabled: formSettings?.email_header_enabled !== false,
        titleEn: formSettings?.email_header_title_en || 'Ministry of Justice',
        titleAr: formSettings?.email_header_title_ar || 'وزارة العدل',
        subtitleEn: formSettings?.email_header_subtitle_en || '',
        subtitleAr: formSettings?.email_header_subtitle_ar || '',
        color: formSettings?.email_header_color || '#1a3a6b',
        gradientEnd: formSettings?.email_header_gradient_end || '#0d2247',
        logoUrl: formSettings?.email_logo_url || formSettings?.header_logo_url || ''
    }

    const sendEmailMutation = useMutation({
        mutationFn: (data) => emailApi.send(submission.id, data),
        onSuccess: () => {
            toast.success(t('email.sent'))
            queryClient.invalidateQueries(['submission', submission.id])
            onClose()
            resetForm()
        },
        onError: () => {
            toast.error(t('email.failed'))
        },
    })

    const resetForm = () => {
        setFormData({
            to: getSubmissionEmail(),
            subject: '',
            message: '',
            subjectAr: '',
            messageAr: '',
        })
        setEmailLanguage('both')
        setActiveTab(0)
    }

    const handleChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }))
    }

    const handleQuickTemplate = (type) => {
        const templates = {
            acknowledgment: {
                subject: 'Thank you for your submission',
                subjectAr: 'شكراً لتقديمك',
                message: 'Thank you for contacting us. We have received your submission and will get back to you shortly.',
                messageAr: 'شكراً لتواصلك معنا. لقد استلمنا طلبك وسنرد عليك قريباً.',
            },
            followUp: {
                subject: 'Following up on your submission',
                subjectAr: 'متابعة بخصوص طلبك',
                message: 'We are following up on your recent submission. Our team is working on your request.',
                messageAr: 'نتابع معك بخصوص طلبك الأخير. فريقنا يعمل على معالجة طلبك.',
            },
            resolution: {
                subject: 'Your request has been resolved',
                subjectAr: 'تم حل طلبك',
                message: 'We are pleased to inform you that your request has been resolved. If you have any further questions, please don\'t hesitate to contact us.',
                messageAr: 'يسرنا إبلاغك بأنه تم حل طلبك. إذا كان لديك أي استفسارات إضافية، لا تتردد في التواصل معنا.',
            },
        }

        if (templates[type]) {
            setFormData((prev) => ({ ...prev, ...templates[type] }))
        }
    }

    const handleSubmit = () => {
        // Validate based on language selection
        if (!formData.to) {
            toast.error('No email address found')
            return
        }

        if (emailLanguage === 'en' || emailLanguage === 'both') {
            if (!formData.subject || !formData.message) {
                toast.error('Please fill in English subject and message')
                return
            }
        }

        if (emailLanguage === 'ar' || emailLanguage === 'both') {
            if (!formData.subjectAr || !formData.messageAr) {
                toast.error('Please fill in Arabic subject and message')
                return
            }
        }

        sendEmailMutation.mutate({
            to: formData.to,
            language: emailLanguage,
            subject: emailLanguage === 'ar' ? formData.subjectAr : formData.subject,
            message: emailLanguage === 'ar' ? formData.messageAr : formData.message,
            subject_en: formData.subject,
            subject_ar: formData.subjectAr,
            message_en: formData.message,
            message_ar: formData.messageAr,
        })
    }

    // Get display subject/message based on language
    const getDisplayContent = () => {
        if (emailLanguage === 'en') {
            return { subject: formData.subject, message: formData.message, showAr: false }
        }
        if (emailLanguage === 'ar') {
            return { subject: formData.subjectAr, message: formData.messageAr, showAr: true }
        }
        return {
            subject: formData.subject,
            message: formData.message,
            subjectAr: formData.subjectAr,
            messageAr: formData.messageAr,
            showBoth: true
        }
    }

    const renderPreview = () => {
        const content = getDisplayContent()
        const year = new Date().getFullYear()

        // Use form settings or defaults
        const HEADER_EN = emailHeaderSettings.titleEn
        const HEADER_AR = emailHeaderSettings.titleAr
        const FOOTER_EN = `Ministry of Justice© ${year} MOJ. All rights reserved.`
        const FOOTER_AR = `وزارة العدل© ${year} MOJ. جميع الحقوق محفوظة.`

        // Get header based on language
        const getHeader = () => {
            if (emailLanguage === 'en') return HEADER_EN
            if (emailLanguage === 'ar') return HEADER_AR
            return `${HEADER_EN} / ${HEADER_AR}`
        }

        // Get subtitle based on language
        const getSubtitle = () => {
            if (emailLanguage === 'en') return emailHeaderSettings.subtitleEn
            if (emailLanguage === 'ar') return emailHeaderSettings.subtitleAr
            const parts = [emailHeaderSettings.subtitleEn, emailHeaderSettings.subtitleAr].filter(Boolean)
            return parts.join(' | ')
        }

        return (
            <Box sx={{ mt: 2 }}>
                <Paper
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: 'background.default'
                    }}
                >
                    {/* Email Header */}
                    <Box sx={{
                        p: 3,
                        background: `linear-gradient(135deg, ${emailHeaderSettings.color} 0%, ${emailHeaderSettings.gradientEnd} 100%)`,
                        color: 'white',
                        textAlign: 'center'
                    }}>
                        {/* Email Logo */}
                        {emailHeaderSettings.logoUrl && (
                            <Box
                                component="img"
                                src={emailHeaderSettings.logoUrl}
                                alt="Logo"
                                sx={{
                                    maxHeight: 60,
                                    maxWidth: 200,
                                    mb: 2,
                                    objectFit: 'contain'
                                }}
                            />
                        )}
                        <Typography variant="h6" fontWeight={600}>
                            {getHeader()}
                        </Typography>
                        {getSubtitle() && (
                            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                                {getSubtitle()}
                            </Typography>
                        )}
                    </Box>

                    {/* Recipient Info */}
                    <Box sx={{ px: 3, py: 1, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('email.to')}: {formData.to}
                        </Typography>
                    </Box>

                    {/* Email Body */}
                    <Box sx={{ p: 3, bgcolor: 'background.paper' }}>
                        {/* English Content */}
                        {(emailLanguage === 'en' || emailLanguage === 'both') && (
                            <Box sx={{ mb: emailLanguage === 'both' ? 3 : 0 }}>
                                <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    sx={{ mb: 2 }}
                                >
                                    {formData.subject || 'Subject'}
                                </Typography>
                                <Typography
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        color: 'text.secondary'
                                    }}
                                >
                                    {formData.message || 'Your message content will appear here...'}
                                </Typography>
                            </Box>
                        )}

                        {emailLanguage === 'both' && <Divider sx={{ my: 3 }} />}

                        {/* Arabic Content */}
                        {(emailLanguage === 'ar' || emailLanguage === 'both') && (
                            <Box sx={{ textAlign: 'right', direction: 'rtl' }}>
                                <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    sx={{ mb: 2 }}
                                >
                                    {formData.subjectAr || 'الموضوع'}
                                </Typography>
                                <Typography
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        color: 'text.secondary'
                                    }}
                                >
                                    {formData.messageAr || '...محتوى رسالتك سيظهر هنا'}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Email Footer */}
                    <Box sx={{ p: 2, bgcolor: 'action.hover', textAlign: 'center' }}>
                        {(emailLanguage === 'en' || emailLanguage === 'both') && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                {FOOTER_EN}
                            </Typography>
                        )}
                        {(emailLanguage === 'ar' || emailLanguage === 'both') && (
                            <Typography variant="caption" color="text.secondary" display="block" dir="rtl">
                                {FOOTER_AR}
                            </Typography>
                        )}
                    </Box>
                </Paper>
            </Box>
        )
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        {t('email.title')}
                        <Typography variant="body2" color="text.secondary">
                            {submission?.submission_id}
                        </Typography>
                    </Box>
                    {/* Language Selection */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Language fontSize="small" color="action" />
                        <ToggleButtonGroup
                            value={emailLanguage}
                            exclusive
                            onChange={(_, v) => v && setEmailLanguage(v)}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 2,
                                    py: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&.Mui-selected': {
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '&:hover': {
                                            bgcolor: 'primary.dark',
                                        }
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="en">EN</ToggleButton>
                            <ToggleButton value="ar">AR</ToggleButton>
                            <ToggleButton value="both">{t('email.both') || 'Both'}</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
            </DialogTitle>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                    <Tab icon={<Edit />} iconPosition="start" label={t('email.compose') || 'Compose'} />
                    <Tab icon={<Visibility />} iconPosition="start" label={t('email.preview') || 'Preview'} />
                </Tabs>
            </Box>

            <DialogContent>
                {activeTab === 0 ? (
                    <>
                        {/* Quick Templates */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                {t('email.templates')}:
                            </Typography>
                            <Chip label={t('email.acknowledgment')} size="small" onClick={() => handleQuickTemplate('acknowledgment')} clickable />
                            <Chip label={t('email.followUp')} size="small" onClick={() => handleQuickTemplate('followUp')} clickable />
                            <Chip label={t('email.resolution')} size="small" onClick={() => handleQuickTemplate('resolution')} clickable />
                        </Box>

                        {/* To Field */}
                        <TextField
                            fullWidth
                            label={t('email.to')}
                            type="email"
                            value={formData.to}
                            margin="normal"
                            required
                            disabled
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon color="primary" />
                                    </InputAdornment>
                                ),
                                readOnly: true,
                            }}
                            helperText={formData.to ? '' : 'No email found in submission data'}
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: 'inherit',
                                    color: 'text.primary',
                                },
                                '& .MuiOutlinedInput-root.Mui-disabled': {
                                    backgroundColor: 'action.hover',
                                }
                            }}
                        />

                        {/* English Fields */}
                        {(emailLanguage === 'en' || emailLanguage === 'both') && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    English Content
                                </Typography>
                                <TextField
                                    fullWidth
                                    label={`${t('email.subject')} (English)`}
                                    value={formData.subject}
                                    onChange={handleChange('subject')}
                                    margin="normal"
                                    required
                                    size="small"
                                />
                                <TextField
                                    fullWidth
                                    label={`${t('email.message')} (English)`}
                                    value={formData.message}
                                    onChange={handleChange('message')}
                                    margin="normal"
                                    multiline
                                    rows={3}
                                    required
                                    size="small"
                                />
                            </Box>
                        )}

                        {/* Arabic Fields */}
                        {(emailLanguage === 'ar' || emailLanguage === 'both') && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom dir="rtl">
                                    المحتوى العربي
                                </Typography>
                                <TextField
                                    fullWidth
                                    label={`${t('email.subject')} (العربية)`}
                                    value={formData.subjectAr}
                                    onChange={handleChange('subjectAr')}
                                    margin="normal"
                                    required={emailLanguage === 'ar' || emailLanguage === 'both'}
                                    size="small"
                                    dir="rtl"
                                />
                                <TextField
                                    fullWidth
                                    label={`${t('email.message')} (العربية)`}
                                    value={formData.messageAr}
                                    onChange={handleChange('messageAr')}
                                    margin="normal"
                                    multiline
                                    rows={3}
                                    required={emailLanguage === 'ar' || emailLanguage === 'both'}
                                    size="small"
                                    dir="rtl"
                                />
                            </Box>
                        )}

                        <Alert severity="info" sx={{ mt: 2 }}>
                            {emailLanguage === 'en' && 'Email will be sent in English only.'}
                            {emailLanguage === 'ar' && 'Email will be sent in Arabic only.'}
                            {emailLanguage === 'both' && 'Email will be sent with both English and Arabic content.'}
                        </Alert>
                    </>
                ) : (
                    <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            This is a preview of how your email will appear to the recipient.
                        </Alert>
                        {renderPreview()}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} disabled={sendEmailMutation.isPending}>
                    {t('common.cancel')}
                </Button>
                {activeTab === 0 ? (
                    <Button
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => setActiveTab(1)}
                    >
                        Preview
                    </Button>
                ) : (
                    <Button
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => setActiveTab(0)}
                    >
                        Edit
                    </Button>
                )}
                <Button
                    variant="contained"
                    startIcon={sendEmailMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <Send />}
                    onClick={handleSubmit}
                    disabled={sendEmailMutation.isPending}
                >
                    {sendEmailMutation.isPending ? t('email.sending') : t('email.send')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default EmailModal
